// ============================================================
// useKeyboardShortcuts — Global keyboard shortcut handler
// Ctrl+S: Save fit, Ctrl+I: Import, Ctrl+E: Export, Ctrl+N: New fit
// Escape: Close modals/context menus
// ============================================================
import { onMounted, onUnmounted, type Ref } from 'vue';

export interface ShortcutCallbacks {
  onSave: () => void;
  onImport: () => void;
  onExport: () => void;
  onNewFit: () => void;
  onEscape: () => void;
}

/**
 * Registers global keyboard shortcuts. Respects input focus —
 * won't fire shortcuts when typing in an input/textarea/contenteditable.
 */
export function useKeyboardShortcuts(callbacks: ShortcutCallbacks): void {
  function isEditableTarget(e: KeyboardEvent): boolean {
    const el = e.target as HTMLElement;
    if (!el) return false;
    const tag = el.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function handleKeyDown(e: KeyboardEvent): void {
    // Escape always works, even in inputs
    if (e.key === 'Escape') {
      callbacks.onEscape();
      return;
    }

    // Don't intercept shortcuts when typing in editable fields
    if (isEditableTarget(e)) return;

    const ctrl = e.ctrlKey || e.metaKey;
    if (!ctrl) return;

    switch (e.key.toLowerCase()) {
      case 's':
        e.preventDefault();
        callbacks.onSave();
        break;
      case 'i':
        e.preventDefault();
        callbacks.onImport();
        break;
      case 'e':
        e.preventDefault();
        callbacks.onExport();
        break;
      case 'n':
        e.preventDefault();
        callbacks.onNewFit();
        break;
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });
}
